import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./components/ThemeProvider";
import { LoginPage, OAuthSessionHandler, ProtectedRoute } from "./components/Auth";
import Layout from "./components/Layout";
import Operations from "./pages/Operations";
import SupportWorkspace from "./pages/SupportWorkspace";
import { isSupportTab, isNativeSupportTab } from "./lib/supportSession";
import NativeSupportBoundary from './components/NativeSupportBoundary';
import "@/App.css";

// Load each secondary workspace only when visited; Today stays in the initial bundle.
const FormsWorkspace = lazy(() => import("./components/FormsWorkspace"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const OrchestratorDashboard = lazy(() => import("./pages/OrchestratorDashboard"));
const SpecializedAgents = lazy(() => import("./pages/SpecializedAgents"));
const AgentActions = lazy(() => import("./pages/AgentActions"));
const Automations = lazy(() => import("./pages/Automations"));
const Connectors = lazy(() => import("./pages/Connectors"));
const PortalDesk = lazy(() => import("./pages/PortalDesk"));
const PortalDeskV2 = lazy(() => import("./pages/PortalDeskV2"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const AdminMarketplace = lazy(() => import("./pages/AdminMarketplace"));
const Reports = lazy(() => import("./pages/Reports"));
const EntityCache = lazy(() => import("./pages/EntityCache"));
const DeveloperDocs = lazy(() => import("./pages/DeveloperDocs"));
const Settings = lazy(() => import("./pages/Settings"));
const Monitors = lazy(() => import("./pages/Monitors"));
const Skills = lazy(() => import("./pages/Skills"));
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
const OperationsBriefings = lazy(() => import("./pages/OperationsBriefings"));
const OperationsActivity = lazy(() => import("./pages/OperationsActivity"));
const OperationsCanvases = lazy(() => import("./pages/OperationsCanvases"));
const OperationsFunctions = lazy(() => import("./pages/OperationsFunctions"));
const OperationsEntities = lazy(() => import("./pages/OperationsEntities"));
const OperationsCoverage = lazy(() => import("./pages/OperationsCoverage"));
const SkillWorkspaces = lazy(() => import("./pages/SkillWorkspaces"));
const SkillWorkspace = lazy(() => import("./pages/SkillWorkspace"));
const PublicForm = lazy(() => import("./pages/PublicForm"));
const RpaOperations = lazy(() => import("./pages/RpaOperations"));
const BusinessObligations = lazy(() => import("./pages/BusinessObligations"));
const ScheduledTriggers = lazy(() => import("./pages/ScheduledTriggers"));
const ContinuationPolicies = lazy(() => import("./pages/ContinuationPolicies"));
const WorkflowConversations = lazy(() => import("./pages/WorkflowConversations"));
const DocumentStudio = lazy(() => import("./pages/DocumentStudio"));
const SOPs = lazy(() => import("./pages/SOPs"));

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export function AppRouter() {
  const location = useLocation();

  const nativeSupport = location.pathname === '/support/native' || isNativeSupportTab();
  if ((location.pathname.startsWith('/support/') || isSupportTab()) && !nativeSupport) {
    return <SupportWorkspace />;
  }

  const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
  if (!nativeSupport && hashParams.get("session_token")) {
    return <OAuthSessionHandler />;
  }

  const routes = (
    <Routes>
      <Route path="/support/native" element={<p role="status">Opening customer workspace…</p>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forms/respond" element={<PublicForm />} />
      {process.env.NODE_ENV !== "production" && (
        <Route path="/obligations-preview" element={<BusinessObligations demo />} />
      )}
      {process.env.NODE_ENV !== "production" && (
        <Route
          path="/automations-preview"
          element={<div className="min-h-screen bg-background p-4 md:p-8"><Automations demo /></div>}
        />
      )}
      {process.env.NODE_ENV !== "production" && (
        <Route
          path="/continuations-preview"
          element={<div className="min-h-screen bg-background p-4 md:p-8"><ScheduledTriggers demo /></div>}
        />
      )}
      {process.env.NODE_ENV !== "production" && (
        <Route
          path="/policies-preview"
          element={<div className="min-h-screen bg-background p-4 md:p-8"><ContinuationPolicies demo /></div>}
        />
      )}
      {process.env.NODE_ENV !== "production" && (
        <Route path="/documents-preview" element={<div className="min-h-screen bg-background p-4 md:p-8"><DocumentStudio demo /></div>} />
      )}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Operations />} />
        <Route path="briefings" element={<OperationsBriefings />} />
        <Route path="activity" element={<OperationsActivity />} />
        <Route path="canvases" element={<OperationsCanvases />} />
        <Route path="functions" element={<OperationsFunctions />} />
        <Route path="entities" element={<OperationsEntities />} />
        <Route path="fleet" element={<OperationsEntities />} />
        <Route path="forms" element={<FormsWorkspace />} />
        <Route path="skill-workspaces" element={<SkillWorkspaces />} />
        <Route path="skill-workspaces/:skillId" element={<SkillWorkspace />} />
        <Route path="sops" element={<SOPs />} />
        <Route path="manage/dashboard" element={<Dashboard />} />
        <Route path="manage/coverage" element={<OperationsCoverage />} />
        <Route path="orchestrator" element={<OrchestratorDashboard />} />
        <Route path="agents" element={<SpecializedAgents />} />
        <Route path="skills" element={<Skills />} />
        <Route path="actions" element={<AgentActions />} />
        <Route path="automations" element={<Automations />} />
        <Route path="obligations" element={<BusinessObligations />} />
        <Route path="continuations" element={<ScheduledTriggers />} />
        <Route path="policies" element={<ContinuationPolicies />} />
        <Route path="conversations" element={<WorkflowConversations />} />
        <Route path="schedules" element={<ScheduledTriggers />} />
        <Route path="connectors" element={<Connectors />} />
        <Route path="rpa" element={<RpaOperations />} />
        <Route path="portal-desk" element={<PortalDesk />} />
        <Route path="portal-desk-v2" element={<PortalDeskV2 />} />
        <Route path="marketplace" element={<Marketplace />} />
        <Route path="admin/marketplace" element={<AdminMarketplace />} />
        <Route path="reports" element={<Reports />} />
        <Route path="documents" element={<DocumentStudio />} />
        <Route path="entity-cache" element={<EntityCache />} />
        <Route path="monitors" element={<Monitors />} />
        <Route path="developer" element={<DeveloperDocs />} />
        <Route path="settings" element={<Settings />} />
        <Route path="design-system" element={<DesignSystem />} />
      </Route>
    </Routes>
  );
  const deferredRoutes = <Suspense fallback={<p role="status" className="p-4 text-sm text-muted-foreground">Loading workspace…</p>}>{routes}</Suspense>;
  return nativeSupport ? <NativeSupportBoundary>{deferredRoutes}</NativeSupportBoundary> : deferredRoutes;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
